import humanizeString from 'humanize-string';
import { isBoolean, isPlainObject } from 'lodash-es';
import React, { ReactNode } from 'react';

import { Moment } from 'shared/components/internal/Moment';

import { LabeledPropertyType } from '../shared';
import { getRecordRenderMap, recordNameMap } from './recordMap';

export interface RecordProperty {
  key: string;
  label: string;
  value: any;
}

// complexity 13
// eslint-disable-next-line complexity
function parseValue(key: string, value: any, record: any) {
  const { type } = record;
  const map = getRecordRenderMap();
  const recordProps = map.hasOwnProperty(type) ? map[type] : [];
  const matchingProp = recordProps.hasOwnProperty(key) && recordProps[key];
  const renderer = matchingProp && matchingProp.renderer;

  // If a renderer is defined, call it to render the property. Otherwise,
  // infer which values can be parsed based on the provided keys and values.
  let result: ReactNode = value;
  if (renderer) {
    result = renderer(value, record);
  } else {
    switch (key) {
      case 'timestamp':
        result = <Moment unix={true}>{value}</Moment>;
        break;
      case 'type':
        if (recordNameMap.hasOwnProperty(value)) {
          result = recordNameMap[value];
        }
    }
  }

  if (isBoolean(result)) {
    result = result ? 'True' : 'False';
  }

  if (!result && result !== 0) {
    result = undefined;
  }

  // Leave name undefined if there is no matching label.
  return {
    name: matchingProp && matchingProp.label,
    value: result,
  };
}

export const parseRecord = (record: any, showEmpty: boolean) => {
  const properties: LabeledPropertyType[] = [];

  const parse = (data: any, parent: string[] = []) => {
    const pushProp = (
      value: any,
      parent: string[],
      key?: string,
      name?: string
    ) => {
      if (showEmpty || value !== undefined) {
        // If a label is specified, convert all its spaces to hyphens in the
        // key. It will be parsed when sorted and rendered.
        const label = name && name.split(' ').join('-');
        const propKey = [...parent, key || label].join('-');
        properties.push({
          name: propKey,
          label: humanizeString(propKey),
          value,
        });
      }
    };

    if (!Array.isArray(data) && !isPlainObject(data)) {
      pushProp(data, parent);
    } else {
      for (const key in data) {
        const val = data[key];

        if (isPlainObject(val)) {
          parse(val, [...parent, key]);
        } else if (Array.isArray(val)) {
          if (val.length) {
            val.forEach((arrVal, index) =>
              parse(arrVal, [...parent, key, (index + 1).toLocaleString()])
            );
          } else {
            // Empty arrays should render as "none".
            pushProp(undefined, parent, key);
          }
        } else {
          const { name, value } = parseValue(
            [...parent, key].join('.'),
            val,
            record
          );

          pushProp(value, parent, key, name);
        }
      }
    }
  };

  if (record) {
    parse(record);
  }

  return properties;
};
